import { API } from '@/src/api/api'
import { queryKeys } from '@/src/api/queryKeys'
import { ALL_LOCATIONS, useLocationsContext } from '@/src/contexts/LocationsContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { useLoadedStrategyMetadata } from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import type {
  LoadPlanningAdjustmentFormEmptyable,
  LoadPlanningAdjustmentFormValidated,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { mapFormDataToPreviewDTO } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/loadPlanning.mapper'
import { normalizeDataPointTimestamp } from '@/src/features/procurement-and-markets/utils/procurementTimezone.utils'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { useEffect, useState } from 'react'

export const useLoadPlanningDataCurvePreview = (
  formData: LoadPlanningAdjustmentFormValidated | undefined,
  granularity: GranularityEnum
) => {
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId: companyId } = useLocationsContext()
  const year = Number(strategyMetadata?.year)
  const energyType = strategyMetadata?.energyType

  // Company-scoped adjustments use the ALL_LOCATIONS sentinel in the form; the backend expects no locationId for them.
  const previewLocationId = formData?.locationId === ALL_LOCATIONS ? undefined : formData?.locationId

  return useQuery({
    queryKey: queryKeys.procurement.previewForecast.byParams.toKeyWithArgs({
      companyId,
      year,
      energyType,
      granularity: granularity,
      locationId: previewLocationId,
      requestBody: formData,
      id: formData?.id,
    }),
    queryFn: async () => {
      if (!formData) throw new Error('Form data is required')

      const requestBody = mapFormDataToPreviewDTO(formData)

      const payload = await API.procurements.fetchPreviewForecast({
        companyId: companyId!,
        year,
        energyType: energyType!,
        locationId: previewLocationId,
        include15MinResults: granularity === GranularityEnum.FIFTEEN_MINUTES,
        includeHourlyResults: granularity === GranularityEnum.ONE_HOUR,
        requestBody,
      })

      return {
        ...payload,
        hourlyData: payload.hourlyData?.map(normalizeDataPointTimestamp),
        monthlyData: payload.monthlyData?.map(normalizeDataPointTimestamp),
      }
    },
    placeholderData: keepPreviousData,
    enabled: !!companyId && !!year && !!energyType && !!formData?.locationId && !!formData,
  })
}

export const useLoadPlanningPreview = (formik: FormikProps<LoadPlanningAdjustmentFormEmptyable>) => {
  const [isCalculated, setIsCalculated] = useState(false)
  const [dataToCalculate, setDataToCalculate] = useState<LoadPlanningAdjustmentFormValidated | undefined>(undefined)

  const onCalculate = async () => {
    const errors = await formik.validateForm()
    if (Object.keys(errors).length === 0) {
      setIsCalculated(true)
      setDataToCalculate(formik.values as LoadPlanningAdjustmentFormValidated)
    }
  }

  // useEffect to invalidate the calculation if if the user changes the field again.
  // Unfortunately formik doesnt have some kind of "onChange" hook. That would be a better approach
  useEffect(() => {
    if (isCalculated) {
      setIsCalculated(false)
    }
  }, [formik.values])

  return {
    onCalculate,
    isCalculated,
    dataToCalculate,
  }
}
